import React, { useEffect } from 'react';
import { Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import FormInput from '../ReactHookFormCustom/FormInput';
import FormTextArea from '../ReactHookFormCustom/FormTextArea';
import FormDateTime from '../ReactHookFormCustom/FormDateTime';
import FormAsyncSelect from '../ReactHookFormCustom/FormAsyncSelect';
import { getApplicantsForSelect } from '../../Services/applicantService';
import debounce from 'debounce-promise';

const CalendarEventModal = ({
  showCalendarEventModal,
  onHandleValidSubmit,
  calendarEvent,
  modalPurpose,
  onDelete,
  onToggle,
}) => {
  const { t } = useTranslation();

  const { handleSubmit, reset, control } = useForm({});

  useEffect(() => {
    reset({
      calendarEventDayStart: calendarEvent?.start,
      calendarEventTimeStart: calendarEvent?.start,
      calendarEventTimeEnd: calendarEvent?.end,
      calendarEventTitle: calendarEvent?.title,
      notesTextArea: calendarEvent?.description,
      calendarEventId: calendarEvent?.id,
    });
  }, [calendarEvent]);

  const handleSubmitWithValidations = (values) => {
    const dayStart = new Date(values.calendarEventDayStart);
    const timeStart = new Date(values.calendarEventTimeStart);
    const timeEnd = new Date(values.calendarEventTimeEnd);

    // Extract the date from dayStart and time from timeStart and timeEnd
    const mergedTimeStart = new Date(
      dayStart.getFullYear(),
      dayStart.getMonth(),
      dayStart.getDate(),
      timeStart.getHours(),
      timeStart.getMinutes()
    );

    const mergedTimeEnd = new Date(
      dayStart.getFullYear(),
      dayStart.getMonth(),
      dayStart.getDate(),
      timeEnd.getHours(),
      timeEnd.getMinutes()
    );

    // Replace the values
    values.calendarEventTimeStart = mergedTimeStart;
    values.calendarEventTimeEnd = mergedTimeEnd;

    onHandleValidSubmit(values);
  };

  const getApplicants = async (inputValue) => {
    const { data: applicants } = await getApplicantsForSelect(inputValue);
    return applicants;
  };

  // Create a debounced version of your getCompetences function
  const debouncedGetApplicants = debounce((inputValue, callback) => {
    getApplicants(inputValue).then((result) => {
      callback(result);
    });
  }, 500);

  const promiseOptions = (inputValue, callback) => {
    debouncedGetApplicants(inputValue, callback);
  };

  const defaultAttendees = (eventData) => {
    return eventData?.attendees?.map((attendee) => ({
      value: attendee.value,
      label: attendee.label,
      emailAttendeeType: attendee.emailAttendeeType,
      tenantId: attendee.tenantId,
    }));
  };

  return (
    <Modal isOpen={showCalendarEventModal} centered>
      <ModalHeader>
        <em className="fa-2x icon-calendar"> {t('calendar.TITLE')}</em>
      </ModalHeader>
      <form onSubmit={handleSubmit(handleSubmitWithValidations)}>
        <ModalBody>
          <Row>
            <FormInput
              control={control}
              name="calendarEventId"
              id="calendarEvent_Id"
              value={calendarEvent.id}
              disabled
              hidden
            />
            <Col>
              <FormDateTime
                control={control}
                label={t('calendar.DATE')}
                name="calendarEventDayStart"
                id="calendarEventDayStartId"
                timeFormat={false}
                rules={{ required: t('common.REQUIRED') }}
              />
            </Col>
            <Col className="px-0 mx-0">
              <FormDateTime
                control={control}
                label={t('calendar.START')}
                name="calendarEventTimeStart"
                id="calendarEventTimeStartId"
                dateFormat={false}
                timeFormat="HH:mm"
                timeConstraints={{ minutes: { step: 10 } }}
                rules={{ required: t('common.REQUIRED') }}
              />
            </Col>
            <Col>
              <FormDateTime
                control={control}
                label={t('calendar.END')}
                name="calendarEventTimeEnd"
                dateFormat={false}
                timeFormat="HH:mm"
                timeConstraints={{ minutes: { step: 10 } }}
                rules={{ required: t('common.REQUIRED') }}
              />
            </Col>
          </Row>
          <Row className="pt-2">
            <Col>
              <FormAsyncSelect
                cacheOptions
                defaultOptions
                label={t('calendar.SELECT_ATTENDEES')}
                control={control}
                name="asyncSelectAttendees"
                loadOptions={promiseOptions}
                placeholder={t('common.TYPE_TO_SEARCH')}
                noOptionsMessage={() => t('common.NOTHING_FOUND')}
                rules={{ required: t('common.REQUIRED') }}
                isMulti
                defaultValue={defaultAttendees(calendarEvent)}
              />
            </Col>
          </Row>
          <Row className="pt-2">
            <Col>
              <FormInput
                control={control}
                name="calendarEventTitle"
                id="calendarEventTitleId"
                label={t('calendar.EVENT_TITLE')}
                rules={{ required: t('common.REQUIRED') }}
              />
            </Col>
          </Row>
          <Row className="pt-1">
            <Col>
              <FormTextArea
                className
                control={control}
                label={t('common.DESCRIPTION')}
                name="notesTextArea"
                id="notesTextAreaId"
                maxLength={1000}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <div>
            <Button color="primary">{t('common.SAVE')}</Button>
            &nbsp;
            {modalPurpose === 'edit' && (
              <Button color="danger" onClick={() => onDelete(calendarEvent.id)}>
                {t('common.DELETE')}
              </Button>
            )}
            &nbsp;
            <Button color="secondary" onClick={() => onToggle('close')}>
              {t('common.CANCEL')}
            </Button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default CalendarEventModal;
